import { useState, Fragment } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  Car, CreditCard, Monitor, Building2, Pencil, Trash2,
  X, Loader2, ChevronDown, ChevronUp, Bell, AlertTriangle,
  CheckCircle, Clock, ClipboardCheck, Eye, EyeOff, Upload, Fuel, Radio,
  Wrench, FileDown
} from "lucide-react";
import {
  STATUT_OPTIONS, STATUT_COLORS, STATUT_LABELS, INSPECTION_STATUT,
  FREQUENCE_OPTIONS, CARD_BRANDS
} from "./outilsConstants";

/* ═══════════════════════════════════════════════════════════
   StatusBadge
   ═══════════════════════════════════════════════════════════ */
export function StatusBadge({ value }) {
  const cls = STATUT_COLORS[value] || "bg-slate-500/15 text-slate-400 border-slate-500/20";
  const label = STATUT_LABELS[value] || value;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${cls}`}>{label}</span>;
}

/* ═══════════════════════════════════════════════════════════
   CardBrandBadge
   ═══════════════════════════════════════════════════════════ */
export function CardBrandBadge({ type }) {
  const brand = CARD_BRANDS[type];
  if (!brand) return <span className="text-xs text-slate-400">{type || "—"}</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border"
      style={{ background: brand.color + "20", borderColor: brand.color + "40", color: brand.accent }}>
      {brand.label}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════
   FormField
   ═══════════════════════════════════════════════════════════ */
export function FormField({ field, value, onChange, isDark, vehicleLookup, personneLookup }) {
  const inputCls = isDark
    ? "w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]"
    : "w-full bg-white border border-gray-200 text-gray-900 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]";
  if (field.type === "textarea") return <textarea className={inputCls} rows={3} value={value || ""} onChange={e => onChange(e.target.value)} />;
  if (field.type === "statut") return (
    <select className={inputCls} value={value || "actif"} onChange={e => onChange(e.target.value)}>
      {STATUT_OPTIONS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
    </select>
  );
  if (field.type === "select") return (
    <select className={inputCls} value={value || ""} onChange={e => onChange(e.target.value)}>
      <option value="">—</option>
      {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (field.type === "pin") return <PinField value={value} onChange={onChange} isDark={isDark} />;
  if (field.type === "vehicule_select") return (
    <select className={inputCls} value={value || ""} onChange={e => onChange(e.target.value)}>
      <option value="">Aucun</option>
      {vehicleLookup.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
    </select>
  );
  if (field.type === "personne_select") return (
    <select className={inputCls} value={value || ""} onChange={e => onChange(e.target.value)}>
      <option value="">Aucun</option>
      {personneLookup.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
    </select>
  );
  return <input type={field.type || "text"} className={inputCls} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ""} />;
}

/* ═══════════════════════════════════════════════════════════
   PinField
   ═══════════════════════════════════════════════════════════ */
export function PinField({ value, onChange, isDark }) {
  const [show, setShow] = useState(false);
  const inputCls = isDark
    ? "flex-1 bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]"
    : "flex-1 bg-white border border-gray-200 text-gray-900 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]";
  return (
    <div className="flex items-center gap-2">
      <input type={show ? "text" : "password"} className={inputCls} value={value || ""} onChange={e => onChange(e.target.value)} maxLength={6} placeholder="****" />
      <button type="button" onClick={() => setShow(!show)} className="p-2 rounded-lg hover:bg-white/10" style={{ color: "var(--text-muted)" }}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   EcheanceBanner
   ═══════════════════════════════════════════════════════════ */
export function EcheanceBanner({ echeances, onRefresh }) {
  if (!echeances || echeances.length === 0) return null;
  const urgent = echeances.filter(e => e.expired || e.urgent);
  return (
    <div className="mb-4 rounded-xl border p-4" style={{ background: urgent.length > 0 ? "rgba(239,68,68,0.05)" : "rgba(234,179,8,0.05)", borderColor: urgent.length > 0 ? "rgba(239,68,68,0.2)" : "rgba(234,179,8,0.2)" }} data-testid="echeance-banner">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {urgent.length > 0 ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <Bell className="w-4 h-4 text-yellow-400" />}
          <span className="text-sm font-bold" style={{ color: urgent.length > 0 ? "#ef4444" : "#eab308" }}>
            {echeances.length} echeance{echeances.length > 1 ? "s" : ""} a venir
          </span>
        </div>
        <button onClick={onRefresh} className="text-[10px] px-2.5 py-1 rounded-lg border transition-colors hover:opacity-80" style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }} data-testid="refresh-rappels-btn">
          Verifier maintenant
        </button>
      </div>
      <div className="space-y-1.5 max-h-28 overflow-y-auto">
        {echeances.slice(0, 5).map((e, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${e.expired || e.urgent ? "bg-red-500" : "bg-yellow-500"}`} />
            <span style={{ color: "var(--text-secondary)" }}><strong>{e.name || e.entity_label || "-"}</strong> — {e.label || e.field_label} : {e.date ? new Date(e.date).toLocaleDateString("fr-FR") : ""}</span>
            <span className="text-[10px] ml-auto" style={{ color: e.expired ? "#ef4444" : e.urgent ? "#f97316" : "#eab308" }}>
              {e.expired ? "Depasse !" : `J-${e.days_left ?? e.days_until ?? "?"}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   OutilsDashboard
   ═══════════════════════════════════════════════════════════ */
export function OutilsDashboard({ stats, echeances }) {
  const kpis = [
    { label: "Vehicules", value: stats.vehicules || 0, color: "#f7c935" },
    { label: "Equipements IT", value: stats.equipements || 0, color: "#3b82f6" },
    { label: "Moyens paiement", value: stats.paiements || 0, color: "#22c55e" },
    { label: "Locaux", value: stats.locaux || 0, color: "#8b5cf6" },
    { label: "Carte carburant", value: stats.carburant || 0, color: "#f97316" },
    { label: "Telepeage", value: stats.telepeage || 0, color: "#06b6d4" },
  ];
  const chartData = kpis.map(k => ({ name: k.label, value: k.value }));
  return (
    <div className="mb-6">
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
        {kpis.map(k => (
          <div key={k.label} className="rounded-xl border p-3 text-center" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid={`stat-${k.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-xl font-black" style={{ color: k.color }}>{k.value}</div>
            <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>{k.label}</div>
          </div>
        ))}
      </div>
      {Object.values(stats).some(v => v > 0) && (
        <div className="rounded-xl border p-4 h-40" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: "var(--text-dimmed)", fontSize: 10 }} />
              <YAxis tick={{ fill: "var(--text-dimmed)", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#14101c", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 }} />
              <Bar dataKey="value" fill="#f7c935" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   InvoiceWidget
   ═══════════════════════════════════════════════════════════ */
export function InvoiceWidget({ entityType, entityId, axiosAuth }) {
  const [invoices, setInvoices] = useState([]);
  const [uploading, setUploading] = useState(false);

  useState(() => {
    (async () => {
      try {
        const res = await axiosAuth().get(`/outils/invoices/${entityType}/${entityId}`);
        setInvoices(res.data || []);
      } catch {}
    })();
  });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axiosAuth().post(`/outils/invoices/${entityType}/${entityId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await axiosAuth().get(`/outils/invoices/${entityType}/${entityId}`);
      setInvoices(res.data || []);
    } catch {}
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="space-y-2" data-testid={`invoice-widget-${entityType}-${entityId}`}>
      <div className="flex items-center gap-2 mb-2">
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.jpg,.png,.docx" />
          <span className="text-[10px] px-2.5 py-1 rounded-lg border inline-flex items-center gap-1 transition-colors hover:opacity-80" style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? "Envoi..." : "Ajouter une facture"}
          </span>
        </label>
        <span className="text-[10px]" style={{ color: "var(--text-dimmed)" }}>{invoices.length} facture{invoices.length !== 1 ? "s" : ""}</span>
      </div>
      {invoices.map(inv => (
        <div key={inv.invoice_id} className="flex items-center gap-3 p-2.5 rounded-lg border text-xs" style={{ background: "var(--bg-card)", borderColor: "var(--border-secondary)" }}>
          <CreditCard className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--accent)" }} />
          <span className="flex-1 truncate" style={{ color: "var(--text-secondary)" }}>{inv.filename || "Facture"}</span>
          <span style={{ color: "var(--text-dimmed)" }}>{inv.date ? new Date(inv.date).toLocaleDateString("fr-FR") : ""}</span>
          {inv.amount && <span className="font-bold" style={{ color: "var(--accent)" }}>{inv.amount} EUR</span>}
          {inv.url && <a href={inv.url} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: "var(--accent)" }}>Voir</a>}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MaintenancePanel
   ═══════════════════════════════════════════════════════════ */
export function MaintenancePanel({ vehiculeId, vehiculeLabel, axiosAuth, isDark }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  useState(() => {
    (async () => {
      try { const r = await axiosAuth().get(`/outils/vehicules/${vehiculeId}/maintenance`); setItems(r.data || []); } catch {}
      setLoading(false);
    })();
  });

  const openCreate = () => { setEditItem(null); setForm({ type: "", date: "", km: "", description: "", cout: "", lieu: "", prochaine_date: "" }); setShowForm(true); };
  const openEdit = (it) => { setEditItem(it); setForm({ type: it.type || "", date: it.date || "", km: it.km || "", description: it.description || "", cout: it.cout || "", lieu: it.lieu || "", prochaine_date: it.prochaine_date || "" }); setShowForm(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) await axiosAuth().put(`/outils/vehicules/${vehiculeId}/maintenance/${editItem.maintenance_id}`, form);
      else await axiosAuth().post(`/outils/vehicules/${vehiculeId}/maintenance`, form);
      const r = await axiosAuth().get(`/outils/vehicules/${vehiculeId}/maintenance`); setItems(r.data || []);
      setShowForm(false);
    } catch { alert("Erreur"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try { await axiosAuth().delete(`/outils/vehicules/${vehiculeId}/maintenance/${id}`); const r = await axiosAuth().get(`/outils/vehicules/${vehiculeId}/maintenance`); setItems(r.data || []); } catch {}
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const r = await axiosAuth().get(`/outils/vehicules/${vehiculeId}/maintenance/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `maintenance_${vehiculeLabel.replace(/\s/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Erreur export PDF"); }
    setExporting(false);
  };

  const inputCls = isDark
    ? "w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]"
    : "w-full bg-white border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]";

  return (
    <div data-testid={`maintenance-panel-${vehiculeId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4" style={{ color: "var(--accent)" }} />
          <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Maintenance — {vehiculeLabel}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--accent-bg)", color: "var(--accent)" }}>{items.length}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportPDF} disabled={exporting || items.length === 0}
            className="text-[10px] px-2.5 py-1 rounded-lg border flex items-center gap-1 transition-colors hover:opacity-80 disabled:opacity-40"
            style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }} data-testid="export-maint-pdf">
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} PDF
          </button>
          <button onClick={openCreate} className="text-[10px] px-2.5 py-1 rounded-lg font-bold" style={{ background: "var(--accent)", color: "#14101c" }} data-testid="add-maintenance-btn">
            + Intervention
          </button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-lg border p-4 mb-3 space-y-3" style={{ background: "var(--bg-card)", borderColor: "var(--border-secondary)" }}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Type</label>
              <select className={inputCls} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="">—</option><option value="Vidange">Vidange</option><option value="Freins">Freins</option><option value="Pneus">Pneus</option><option value="Revision">Revision</option><option value="Reparation">Reparation</option><option value="CT">Controle Technique</option><option value="Autre">Autre</option>
              </select>
            </div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Date</label><input type="date" className={inputCls} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Km</label><input type="number" className={inputCls} value={form.km} onChange={e => setForm({ ...form, km: e.target.value })} /></div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Cout (EUR)</label><input type="number" className={inputCls} value={form.cout} onChange={e => setForm({ ...form, cout: e.target.value })} /></div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Lieu / Garage</label><input type="text" className={inputCls} value={form.lieu} onChange={e => setForm({ ...form, lieu: e.target.value })} /></div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Prochaine echeance</label><input type="date" className={inputCls} value={form.prochaine_date} onChange={e => setForm({ ...form, prochaine_date: e.target.value })} /></div>
          </div>
          <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Description</label><textarea className={inputCls} rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="text-xs px-3 py-1.5 rounded-lg border" style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }}>Annuler</button>
            <button onClick={handleSave} disabled={saving} className="text-xs px-3 py-1.5 rounded-lg font-bold disabled:opacity-50" style={{ background: "var(--accent)", color: "#14101c" }}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : editItem ? "Modifier" : "Creer"}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto" style={{ color: "var(--accent)" }} /></div> : items.length === 0 ? (
        <div className="text-center py-4 text-xs" style={{ color: "var(--text-dimmed)" }}>Aucune intervention enregistree</div>
      ) : (
        <div className="space-y-1.5">
          {items.map(it => (
            <div key={it.maintenance_id} className="flex items-center gap-3 p-2.5 rounded-lg border text-xs group" style={{ background: "var(--bg-card)", borderColor: "var(--border-secondary)" }}>
              <Wrench className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--accent)" }} />
              <span className="font-medium" style={{ color: "var(--text-primary)" }}>{it.type || "—"}</span>
              <span style={{ color: "var(--text-muted)" }}>{it.date ? new Date(it.date).toLocaleDateString("fr-FR") : ""}</span>
              {it.km && <span style={{ color: "var(--text-dimmed)" }}>{Number(it.km).toLocaleString("fr-FR")} km</span>}
              {it.cout && <span className="font-bold" style={{ color: "var(--accent)" }}>{it.cout} EUR</span>}
              <span className="flex-1 truncate" style={{ color: "var(--text-dimmed)" }}>{it.description}</span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(it)} className="p-1 rounded hover:bg-white/10"><Pencil className="w-3 h-3" style={{ color: "var(--text-muted)" }} /></button>
                <button onClick={() => handleDelete(it.maintenance_id)} className="p-1 rounded hover:bg-red-500/10"><Trash2 className="w-3 h-3 text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   InspectionsPanel
   ═══════════════════════════════════════════════════════════ */
export function InspectionsPanel({ localId, localNom, axiosAuth, isDark }) {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editInsp, setEditInsp] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useState(() => {
    (async () => {
      try { const r = await axiosAuth().get(`/outils/locaux/${localId}/inspections`); setInspections(r.data || []); } catch {}
      setLoading(false);
    })();
  });

  const openCreate = () => { setEditInsp(null); setForm({ type: "", date: "", statut: "a_planifier", observations: "", frequence: "annuelle", prochaine_date: "" }); setShowForm(true); };
  const openEdit = (insp) => { setEditInsp(insp); setForm({ type: insp.type || "", date: insp.date || "", statut: insp.statut || "a_planifier", observations: insp.observations || "", frequence: insp.frequence || "annuelle", prochaine_date: insp.prochaine_date || "" }); setShowForm(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editInsp) await axiosAuth().put(`/outils/locaux/${localId}/inspections/${editInsp.inspection_id}`, form);
      else await axiosAuth().post(`/outils/locaux/${localId}/inspections`, form);
      const r = await axiosAuth().get(`/outils/locaux/${localId}/inspections`); setInspections(r.data || []);
      setShowForm(false);
    } catch { alert("Erreur"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try { await axiosAuth().delete(`/outils/locaux/${localId}/inspections/${id}`); const r = await axiosAuth().get(`/outils/locaux/${localId}/inspections`); setInspections(r.data || []); } catch {}
  };

  const inputCls = isDark
    ? "w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]"
    : "w-full bg-white border border-gray-200 text-gray-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#f7c935]";

  return (
    <div data-testid={`inspections-panel-${localId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4" style={{ color: "var(--accent)" }} />
          <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Inspections — {localNom}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--accent-bg)", color: "var(--accent)" }}>{inspections.length}</span>
        </div>
        <button onClick={openCreate} className="text-[10px] px-2.5 py-1 rounded-lg font-bold" style={{ background: "var(--accent)", color: "#14101c" }} data-testid="add-inspection-btn">+ Inspection</button>
      </div>

      {showForm && (
        <div className="rounded-lg border p-4 mb-3 space-y-3" style={{ background: "var(--bg-card)", borderColor: "var(--border-secondary)" }}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Type</label><input type="text" className={inputCls} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} placeholder="Ex: Securite incendie" /></div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Date</label><input type="date" className={inputCls} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Statut</label>
              <select className={inputCls} value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value })}>
                {Object.entries(INSPECTION_STATUT).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Frequence</label>
              <select className={inputCls} value={form.frequence} onChange={e => setForm({ ...form, frequence: e.target.value })}>
                {FREQUENCE_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Prochaine inspection</label><input type="date" className={inputCls} value={form.prochaine_date} onChange={e => setForm({ ...form, prochaine_date: e.target.value })} /></div>
          </div>
          <div><label className="text-[10px] mb-1 block" style={{ color: "var(--text-muted)" }}>Observations</label><textarea className={inputCls} rows={2} value={form.observations} onChange={e => setForm({ ...form, observations: e.target.value })} /></div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="text-xs px-3 py-1.5 rounded-lg border" style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }}>Annuler</button>
            <button onClick={handleSave} disabled={saving} className="text-xs px-3 py-1.5 rounded-lg font-bold disabled:opacity-50" style={{ background: "var(--accent)", color: "#14101c" }}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : editInsp ? "Modifier" : "Creer"}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto" style={{ color: "var(--accent)" }} /></div> : inspections.length === 0 ? (
        <div className="text-center py-4 text-xs" style={{ color: "var(--text-dimmed)" }}>Aucune inspection</div>
      ) : (
        <div className="space-y-1.5">
          {inspections.map(insp => {
            const st = INSPECTION_STATUT[insp.statut] || INSPECTION_STATUT.a_planifier;
            return (
              <div key={insp.inspection_id} className="flex items-center gap-3 p-2.5 rounded-lg border text-xs group" style={{ background: "var(--bg-card)", borderColor: "var(--border-secondary)" }}>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${st.cls}`}>{st.label}</span>
                <span className="font-medium" style={{ color: "var(--text-primary)" }}>{insp.type || "—"}</span>
                <span style={{ color: "var(--text-muted)" }}>{insp.date ? new Date(insp.date).toLocaleDateString("fr-FR") : ""}</span>
                <span className="flex-1 truncate" style={{ color: "var(--text-dimmed)" }}>{insp.observations}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(insp)} className="p-1 rounded hover:bg-white/10"><Pencil className="w-3 h-3" style={{ color: "var(--text-muted)" }} /></button>
                  <button onClick={() => handleDelete(insp.inspection_id)} className="p-1 rounded hover:bg-red-500/10"><Trash2 className="w-3 h-3 text-red-400" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
